import { For, Show, createEffect, onCleanup } from 'solid-js'
import { useLocation, useNavigate } from '@solidjs/router'
import { userStore } from '../../store'
import { CFLogo } from './Logo'
import { IconClose, IconTwitter, IconInstagram, IconFacebook } from './Icons'
import { navSections } from './navData'
import type { NavItem } from './navData'
import { TokenCoin } from './TokenCoin'
import { DiamondIcon } from './DiamondIcon'

export function MobileDrawer(props: { open: boolean; onClose: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = () => userStore()
  const loggedIn = () => user().loggedIn
  const tokens = () => user().profile?.totalAvailableTokens ?? 0
  const handle = () => user().profile?.handle || 'User'

  createEffect(() => {
    document.body.style.overflow = props.open ? 'hidden' : ''
    onCleanup(() => {
      document.body.style.overflow = ''
    })
  })

  const isActive = (route?: string) => {
    if (!route) return false
    if (route === '/') return location.pathname === '/'
    return location.pathname.startsWith(route)
  }

  const handleItemClick = (item: NavItem) => {
    props.onClose()
    if (item.action === 'directory') {
      userStore.directoryModal(true)
    } else if (item.action === 'buyTokens') {
      loggedIn() ? userStore.buyTokensModal(true) : userStore.loginModal(true)
    } else if (item.route) {
      navigate(item.route)
    }
  }

  return (
    <>
      <div class={`cf-drawer-scrim ${props.open ? 'open' : ''}`} onClick={props.onClose} />
      <div class={`cf-drawer ${props.open ? 'open' : ''}`}>
        {/* Header */}
        <div class="cf-dr-head">
          <CFLogo size="md" showSub />
          <button class="cf-dr-close" onClick={props.onClose}>
            <IconClose size={18} />
          </button>
        </div>

        {/* Nav sections — the user card + links scroll together */}
        <div class="cf-dr-body">
          {/* User card (logged in) — scrolls with the menu */}
          <Show when={loggedIn()}>
            <div class="cf-dr-user-card">
              {/* 1. Avatar + display name */}
              <div style={{ display: 'flex', 'align-items': 'center', gap: '12px' }}>
                <div
                  class="cf-ud-avatar"
                  style={{
                    width: '44px',
                    height: '44px',
                    'font-size': '18px',
                    margin: '0',
                    'flex-shrink': '0',
                  }}
                >
                  {(handle()[0] || 'U').toUpperCase()}
                </div>
                <div class="cf-dr-uc-name">{handle()}</div>
              </div>

              {/* 2. VIP Member pill */}
              <div class="cf-dr-uc-vip-row">
                <DiamondIcon size={22} />
                <span class="cf-dr-uc-vip-text">VIP MEMBER</span>
              </div>

              {/* 3. Level row — PLACEHOLDER values pending Rewards backend (#20) */}
              <div class="cf-dr-uc-level-row">
                <div class="cf-dr-uc-level-left">
                  <span class="cf-dr-uc-star">★</span>
                  <span class="cf-dr-uc-level-text">Level 5 · Platinum</span>
                </div>
                <span class="cf-dr-uc-xp-count">1250 / 1500 XP</span>
              </div>
              <div class="cf-dr-uc-xp-bar">
                {/* ~83% fill: 1250/1500 */}
                <div class="cf-dr-uc-xp-fill" style={{ width: '83%' }} />
              </div>

              {/* 4. View Rewards link — the Rewards page lands with item #20; no-op until then
                 (avoids a 404 dead-link). Wire navigate('/rewards') once that route exists. */}
              <button class="cf-dr-uc-rewards-link" onClick={() => {}}>
                <span>VIEW REWARDS</span>
                <span class="cf-dr-uc-rewards-chevron">›</span>
              </button>

              {/* 5. Tokens chip */}
              <div
                class="cf-dr-uc-token-chip"
                onClick={() => {
                  props.onClose()
                  userStore.buyTokensModal(true)
                }}
              >
                <div class="cf-dr-uc-token-left">
                  {/* Gold coin tile */}
                  <div
                    class="cf-sb-wallet-coin-tile"
                    style={{ width: '36px', height: '36px', 'border-radius': '9px' }}
                  >
                    <TokenCoin size={20} />
                  </div>
                  <div class="cf-dr-uc-token-info">
                    <span class="cf-dr-uc-token-balance">{tokens()}</span>
                    <span class="cf-dr-uc-token-label">TOKENS</span>
                  </div>
                </div>
                <button
                  class="cf-dr-uc-buy-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    props.onClose()
                    userStore.buyTokensModal(true)
                  }}
                >
                  + Buy
                </button>
              </div>
            </div>
          </Show>

          <For each={navSections}>
            {(section) => (
              <>
                <div class="cf-dr-section">{section.label}</div>
                <For each={section.items}>
                  {(item) => {
                    if (item.loggedInOnly && !loggedIn()) {
                      return (
                        <button
                          class="cf-dr-link"
                          onClick={() => {
                            props.onClose()
                            userStore.loginModal(true)
                          }}
                        >
                          <span class="cf-sb-icon" style={{ width: '30px', height: '30px' }}>
                            <item.icon size={14} />
                          </span>
                          {item.label}
                        </button>
                      )
                    }
                    if (item.children) {
                      const anyChildActive = () =>
                        item.children?.some((c) => isActive(c.route)) ?? false
                      return (
                        <>
                          <div class={`cf-dr-link ${anyChildActive() ? 'active' : ''}`}>
                            <span class="cf-sb-icon" style={{ width: '30px', height: '30px' }}>
                              <item.icon size={14} />
                            </span>
                            {item.label}
                          </div>
                          <For each={item.children}>
                            {(child) => (
                              <button
                                class={`cf-dr-link cf-dr-sublink ${
                                  isActive(child.route) ? 'active' : ''
                                }`}
                                onClick={() => {
                                  props.onClose()
                                  navigate(child.route)
                                }}
                              >
                                <span class="cf-dr-sub-dot" />
                                {child.label}
                              </button>
                            )}
                          </For>
                        </>
                      )
                    }

                    return (
                      <button
                        class={`cf-dr-link ${item.route && isActive(item.route) ? 'active' : ''}`}
                        onClick={() => handleItemClick(item)}
                      >
                        <span class="cf-sb-icon" style={{ width: '30px', height: '30px' }}>
                          <item.icon size={14} />
                        </span>
                        {item.label}
                      </button>
                    )
                  }}
                </For>
              </>
            )}
          </For>

          {/* Logout */}
          <Show when={loggedIn()}>
            <div style={{ padding: '14px 22px 22px' }}>
              <button
                class="cf-ud-logout"
                style={{ width: '100%', margin: '0' }}
                onClick={() => {
                  props.onClose()
                  userStore.logout()
                }}
              >
                LOG OUT
              </button>
            </div>
          </Show>

          {/* Footer with socials */}
          <div class="cf-sb-footer">
            <div class="cf-sb-socials">
              <a
                class="cf-sb-social"
                href="https://x.com/cosplayfans__"
                target="_blank"
                rel="noopener"
              >
                <IconTwitter size={14} />
              </a>
              <a
                class="cf-sb-social"
                href="https://www.instagram.com/cosplayfansofficial/"
                target="_blank"
                rel="noopener"
              >
                <IconInstagram size={14} />
              </a>
              <a
                class="cf-sb-social"
                href="https://www.facebook.com/cosplayfansofficial/"
                target="_blank"
                rel="noopener"
              >
                <IconFacebook size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
